/**
 * gm-pdp-scroll — scroll fluido das 2 colunas (spec 0063 / ADR-044).
 *
 * As duas colunas sobem juntas (fluxo normal). Quando o bottom de UMA delas
 * (a mais curta) toca o bottom da tela antes da outra, essa coluna trava
 * (position: fixed) até a outra alinhar bottom com bottom — aí solta e
 * ambas voltam a rolar juntas. Na subida, mesma lógica alinhando topo com
 * topo (contra o header). Não importa qual coluna é mais curta (galeria ou
 * info); a que chegar primeiro no limite é a que trava.
 *
 * Se o usuário inverter a direção do scroll enquanto uma coluna está
 * travada, ela destrava NA HORA (independente de onde estiver) — as duas
 * colunas voltam a rolar juntas imediatamente, até uma delas precisar
 * travar de novo. Pra isso não causar um salto visual, "destravar" nunca
 * devolve a coluna ao fluxo nativo do grid direto; em vez disso ela vira
 * `position: absolute` com um offset calculado uma única vez a partir da
 * posição congelada atual (`handoffToOffset`), then rola 1:1 com a página
 * como o resto do fluxo, sem precisar de nenhum update por frame.
 *
 * Troca de tab que muda a altura da info (pin-bottom): handoff preservando
 * o top visual (grow desce o bottom / shrink recolhe o bottom) + spacer.
 * Shrink com info mais curta que a galeria: freezeAtCurrent (fixed no top
 * atual, sem bottom:0) até bottoms alinharem. Pin da galeria só no scroll
 * natural (checkFreeDown), sem re-pin forçado.
 *
 * Classic: early return (CSS sticky). Desktop only (≥750px).
 *
 * Fold chrome no pin-top: `headerBottom()` (sticky, sem topbar) pra o Apple
 * ficar abaixo do viewport na subida — mesmo alinhamento da 1ª tela.
 * Altura da foto fica travada no baseline (`--gm-pdp-main-lock`) pra o
 * delta do chrome virar espaço flex, não resize da imagem (evita glitch
 * quando o topbar volta).
 *
 * Topo (scrollY≈0): unlock por inversão de direção roda ANTES da zona EPS.
 * Direção = qualquer Δy (não +1): passos de 1px no trackpad ainda destravam.
 * Qualquer pin / freeze / offset no topo → hardReset (fluxo livre). Não
 * depender de sticky inactive: após handoffToOffset o `is-offset` ficava
 * preso se o header ainda reportava sticky-active (scroll rápido sobe/desce).
 * Pin-top perto do topo + goingDown → hardReset (não handoff), pra não
 * deixar absolute grudado na 1ª tela.
 *
 * Unlock mid-page (desce → sobe → desce sem tocar y=0): além do Δy por
 * frame, usa extremo de scrollY desde o pin — pin-top solta se y sobe do
 * mínimo visto; pin-bottom solta se y desce do máximo. Δy sozinho falhava
 * com rAF coalescido / smooth (net direction errada por 1 frame).
 * Latch de direção: só checkFreeUp na subida latched; só checkFreeDown na
 * descida (evita re-pin top logo após unlock).
 *
 * Init / refresh com scroll já profundo: reconcileAtRest aplica pin-bottom
 * sem exigir mudança de direção (evaluate em repouso não pinava).
 */
(function () {
  const DESKTOP_MIN = 750;
  const EPS = 8;
  /** Perto do topo: unlock de pin-top vira hardReset (evita is-offset grudado). */
  const NEAR_TOP_Y = 48;
  /** Folga pra unlock por extremo (trackpad jitter). */
  const REVERSE_EPS = 12;

  /** @type {{ key: 'left' | 'right', edge: 'top' | 'bottom' } | null} */
  let pinned = null;
  let lastY = window.scrollY || 0;
  /** Último scrollY visto no evento (mesmo com rAF coalescido). */
  let latestY = window.scrollY || 0;
  /**
   * Direção latched: 1 = descendo, -1 = subindo, 0 = indefinido.
   * Sobrevive a frames com Δy=0 (pausa / smooth).
   * @type {-1 | 0 | 1}
   */
  let scrollDir = 0;
  /**
   * Extremo de scrollY desde o pin atual.
   * pin-top: mínimo (ponto mais alto); pin-bottom: máximo (mais fundo).
   */
  let pinExtremeY = 0;
  /** @type {number} */
  let raf = 0;
  /** @type {HTMLElement | null} */
  let hero = null;
  /** @type {HTMLElement | null} */
  let grid = null;
  /** @type {ResizeObserver | null} */
  let ro = null;
  /** Última altura da coluna info (tabs) — detecta grow sob pin-bottom. */
  let lastRightHeight = 0;
  /**
   * Memória da 1ª tela (fluxo nativo, scrollY≈0) pra travar altura da foto.
   * @type {{ mainHeight: number } | null}
   */
  let baseline = null;

  const cols = {
    left: {
      el: /** @type {HTMLElement | null} */ (null),
      spacer: /** @type {HTMLElement | null} */ (null),
      spacerClass: 'gm-pdp-hero__gallery-spacer',
      /** Posição natural (topo, relativa ao grid) capturada no bind, antes de qualquer offset. */
      rowStart: 0,
    },
    right: {
      el: /** @type {HTMLElement | null} */ (null),
      spacer: /** @type {HTMLElement | null} */ (null),
      spacerClass: 'gm-pdp-hero__info-spacer',
      rowStart: 0,
    },
  };

  /** @param {'left' | 'right'} key */
  function otherKey(key) {
    return key === 'left' ? 'right' : 'left';
  }

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  function isFluid() {
    return hero?.getAttribute('data-gm-pdp-scroll') === 'fluid';
  }

  function headerBottom() {
    const row =
      document.querySelector('#header-component .header__row--top') ||
      document.getElementById('header-component');
    if (!row) return 60;
    return Math.round(row.getBoundingClientRect().bottom);
  }

  /**
   * Pin-top: fold chrome = hb sticky (Apple abaixo do viewport na subida).
   * null = CSS header-group (1ª tela com topbar).
   * @param {number | null} px
   */
  function setFoldChrome(px) {
    if (!hero) return;
    if (px == null) {
      hero.style.removeProperty('--gm-pdp-fold-chrome');
      return;
    }
    hero.style.setProperty('--gm-pdp-fold-chrome', `${Math.round(px)}px`);
  }

  function clearFoldChrome() {
    setFoldChrome(null);
  }

  /** Trava altura da foto principal (baseline) — fold pode crescer sem resize. */
  function lockMainHeight(px) {
    if (!hero || !(px > 0)) return;
    hero.style.setProperty('--gm-pdp-main-lock', `${Math.round(px)}px`);
  }

  function clearMainLock() {
    if (!hero) return;
    hero.style.removeProperty('--gm-pdp-main-lock');
  }

  /** @param {HTMLElement | null} el */
  function colIsStuck(el) {
    if (!el) return false;
    return (
      el.classList.contains('is-offset') ||
      el.classList.contains('is-pin-top') ||
      el.classList.contains('is-pin-bottom') ||
      el.classList.contains('is-pin-freeze')
    );
  }

  /** Snapshot da 1ª tela (fluxo nativo + topo) + trava altura da foto. */
  function captureBaseline() {
    if (!hero || !cols.right.el || !cols.left.el) return;
    if ((window.scrollY || 0) > EPS) return;
    if (colIsStuck(cols.left.el) || colIsStuck(cols.right.el)) return;
    const main = /** @type {HTMLElement | null} */ (
      hero.querySelector('.gm-pdp-hero__main')
    );
    const mainHeight = main ? main.getBoundingClientRect().height : 0;
    baseline = {
      mainHeight,
    };
    if (mainHeight > 0) lockMainHeight(mainHeight);
  }

  /** @param {'left' | 'right'} key */
  function ensureSpacer(key) {
    const col = cols[key];
    if (!col.el || col.spacer) return;
    col.spacer = document.createElement('div');
    col.spacer.className = col.spacerClass;
    col.spacer.setAttribute('aria-hidden', 'true');
    col.el.after(col.spacer);
  }

  /** @param {'left' | 'right'} key */
  function clearPositionStyles(key) {
    const col = cols[key];
    if (!col.el) return;
    col.el.classList.remove('is-pin-top', 'is-pin-bottom', 'is-pin-freeze', 'is-offset');
    col.el.style.top = '';
    col.el.style.left = '';
    col.el.style.width = '';
    col.el.style.right = '';
    col.el.style.bottom = '';
  }

  /**
   * @param {'left' | 'right'} key
   * @param {number} h
   */
  function setSpacerHeight(key, h) {
    const col = cols[key];
    if (!col.spacer) return;
    col.spacer.style.height = `${Math.round(Math.max(0, h))}px`;
    col.spacer.classList.add('is-active');
  }

  /** @param {'left' | 'right'} key */
  function deactivateSpacer(key) {
    const col = cols[key];
    if (!col.spacer) return;
    col.spacer.classList.remove('is-active');
    col.spacer.style.height = '';
  }

  /**
   * Congela a coluna numa borda da viewport (position: fixed).
   * @param {'left' | 'right'} key
   * @param {'top' | 'bottom'} edge
   * @param {{ left: number, width: number, height: number } | null} [fromFlow]
   */
  function applyPin(key, edge, fromFlow) {
    const col = cols[key];
    if (!col.el) return;

    const r = fromFlow || col.el.getBoundingClientRect();
    const { left, width } = r;
    const h = fromFlow ? fromFlow.height : r.height;
    if (!(width > 0)) return;

    clearPositionStyles(key);
    col.el.classList.add(edge === 'top' ? 'is-pin-top' : 'is-pin-bottom');
    col.el.style.left = `${Math.round(left)}px`;
    col.el.style.width = `${Math.round(width)}px`;

    if (edge === 'top') {
      const hb = headerBottom();
      col.el.style.top = `${hb}px`;
      col.el.style.bottom = '';
      // Chrome = hb sticky: Apple some na subida. Foto travada no baseline
      // absorve o delta como espaço flex (sem resize visual).
      if (key === 'right') setFoldChrome(hb);
    } else {
      col.el.style.top = '';
      col.el.style.bottom = '0';
    }

    setSpacerHeight(key, h);
    pinned = { key, edge };
    pinExtremeY = window.scrollY || 0;
  }

  /**
   * Congela a coluna na posição visual atual (fixed top=rect.top).
   * Sem bottom:0 — evita snap à borda da viewport após shrink de tab.
   * Unlock = mesma regra de pin-bottom (outra coluna alcança o bottom).
   * @param {'left' | 'right'} key
   */
  function freezeAtCurrent(key) {
    const col = cols[key];
    if (!col.el) return;

    const r = col.el.getBoundingClientRect();
    if (!(r.width > 0)) return;

    clearPositionStyles(key);
    col.el.classList.add('is-pin-freeze');
    col.el.style.top = `${Math.round(r.top)}px`;
    col.el.style.bottom = '';
    col.el.style.left = `${Math.round(r.left)}px`;
    col.el.style.width = `${Math.round(r.width)}px`;

    setSpacerHeight(key, r.height);
    if (key === 'right') clearFoldChrome();
    pinned = { key, edge: 'bottom' };
    pinExtremeY = window.scrollY || 0;
  }

  /**
   * Destrava a coluna SEM salto visual: em vez de devolver ao fluxo nativo
   * do grid (que pode estar longe da posição congelada atual, causando um
   * pulo), calcula o offset (relativo ao grid) que reproduz exatamente a
   * posição atual e aplica via `position: absolute`. Dali em diante ela
   * rola 1:1 com a página normalmente — sem precisar de updates por frame.
   * @param {'left' | 'right'} key
   */
  function handoffToOffset(key) {
    const col = cols[key];
    if (!col.el || !grid) return;

    const gridRect = grid.getBoundingClientRect();
    const colRect = col.el.getBoundingClientRect();
    const topWithinGrid = colRect.top - gridRect.top;
    const leftWithinGrid = colRect.left - gridRect.left;

    clearPositionStyles(key);
    col.el.classList.add('is-offset');
    col.el.style.top = `${Math.round(topWithinGrid)}px`;
    col.el.style.left = `${Math.round(leftWithinGrid)}px`;
    col.el.style.width = `${Math.round(colRect.width)}px`;

    setSpacerHeight(key, topWithinGrid + colRect.height - col.rowStart);

    if (key === 'right') clearFoldChrome();
    if (pinned && pinned.key === key) pinned = null;
    pinExtremeY = 0;
  }

  /**
   * Pin-bottom + altura mudou: fixed bottom:0 move o top (grow sobe / shrink desce).
   * Handoff absolute com o top *pré-mudança* pra o delta só alterar o bottom.
   * @param {'left' | 'right'} key
   * @param {number} heightDelta - positivo = cresceu; negativo = encolheu
   */
  function handoffPinBottomPreservingTop(key, heightDelta) {
    const col = cols[key];
    if (!col.el || !grid || !(Math.abs(heightDelta) > 0)) return;

    const gridRect = grid.getBoundingClientRect();
    const colRect = col.el.getBoundingClientRect();
    // Após grow: top subiu (−delta no rect); após shrink: top desceu (+|delta|).
    // Em ambos: top pré-mudança = colRect.top + heightDelta.
    const visualTop = colRect.top + heightDelta;
    const topWithinGrid = visualTop - gridRect.top;
    const leftWithinGrid = colRect.left - gridRect.left;

    clearPositionStyles(key);
    col.el.classList.add('is-offset');
    col.el.style.top = `${Math.round(topWithinGrid)}px`;
    col.el.style.left = `${Math.round(leftWithinGrid)}px`;
    col.el.style.width = `${Math.round(colRect.width)}px`;

    setSpacerHeight(key, topWithinGrid + colRect.height - col.rowStart);

    if (key === 'right') clearFoldChrome();
    if (pinned && pinned.key === key) pinned = null;
    pinExtremeY = 0;
  }

  /**
   * Solta pin (near-top = hardReset; mid-page = handoff).
   * @param {'left' | 'right'} key
   * @param {number} y
   */
  function unlockPinned(key, y) {
    if (y <= NEAR_TOP_Y) {
      hardReset('left');
      hardReset('right');
      pinned = null;
      pinExtremeY = 0;
      requestAnimationFrame(captureBaseline);
      return;
    }
    handoffToOffset(key);
  }

  /** Reseta totalmente uma coluna pro fluxo nativo do grid (mobile / classic). */
  function hardReset(key) {
    clearPositionStyles(key);
    deactivateSpacer(key);
    if (key === 'right') clearFoldChrome();
  }

  /**
   * @param {DOMRect} lRect
   * @param {DOMRect} rRect
   * @param {number} vh
   */
  function checkFreeDown(lRect, rRect, vh) {
    // Bottom da coluna mais curta bate no fim da tela; a outra ainda tem
    // mais conteúdo abaixo -> trava a mais curta até a outra alcançar.
    if (lRect.bottom <= vh + EPS && rRect.bottom > vh + EPS) {
      applyPin('left', 'bottom', { left: lRect.left, width: lRect.width, height: lRect.height });
      return;
    }
    if (rRect.bottom <= vh + EPS && lRect.bottom > vh + EPS) {
      applyPin('right', 'bottom', { left: rRect.left, width: rRect.width, height: rRect.height });
    }
  }

  /**
   * @param {DOMRect} lRect
   * @param {DOMRect} rRect
   * @param {number} hb
   */
  function checkFreeUp(lRect, rRect, hb) {
    // Topo da coluna mais curta bate no header; a outra ainda tem mais
    // conteúdo acima -> trava a mais curta até a outra alcançar.
    if (lRect.top >= hb - EPS && rRect.top < hb - EPS) {
      applyPin('left', 'top', { left: lRect.left, width: lRect.width, height: lRect.height });
      return;
    }
    if (rRect.top >= hb - EPS && lRect.top < hb - EPS) {
      applyPin('right', 'top', { left: rRect.left, width: rRect.width, height: rRect.height });
    }
  }

  /**
   * Qual coluna está em pin-top / pin-bottom no DOM (fonte de verdade).
   * Se `pinned` dessincronizar da classList, ainda dá pra destravar.
   * @param {'top' | 'bottom'} edge
   * @returns {'left' | 'right' | null}
   */
  function pinnedKeyFromDom(edge) {
    const cls = edge === 'top' ? 'is-pin-top' : 'is-pin-bottom';
    if (cols.left.el?.classList.contains(cls)) return 'left';
    if (cols.right.el?.classList.contains(cls)) return 'right';
    return null;
  }

  function evaluate() {
    if (!hero || !cols.left.el || !cols.right.el) return;
    if (!isDesktop() || !isFluid()) {
      hardReset('left');
      hardReset('right');
      pinned = null;
      pinExtremeY = 0;
      clearFoldChrome();
      return;
    }

    const hb = headerBottom();
    const vh = window.innerHeight;
    const y = latestY;
    const goingDown = y > lastY;
    const goingUp = y < lastY;
    if (goingDown) scrollDir = 1;
    else if (goingUp) scrollDir = -1;

    const domPinTop = pinnedKeyFromDom('top');
    const domPinBottom = pinnedKeyFromDom('bottom');
    const pinTopKey =
      pinned?.edge === 'top'
        ? pinned.key
        : /** @type {'left'|'right'|null} */ (domPinTop);
    const pinBottomKey =
      pinned?.edge === 'bottom'
        ? pinned.key
        : /** @type {'left'|'right'|null} */ (domPinBottom);

    // Extremo desde o pin: solta mesmo se o Δy do frame falhar (rAF/smooth).
    if (pinTopKey) {
      pinExtremeY = Math.min(pinExtremeY, y);
      if (y > pinExtremeY + REVERSE_EPS || goingDown) {
        unlockPinned(pinTopKey, y);
        scrollDir = 1;
        lastY = y;
        return;
      }
    } else if (pinBottomKey) {
      pinExtremeY = Math.max(pinExtremeY, y);
      if (y < pinExtremeY - REVERSE_EPS || goingUp) {
        unlockPinned(pinBottomKey, y);
        scrollDir = -1;
        lastY = y;
        return;
      }
    }

    // Offset órfão perto do topo na descida.
    if (
      scrollDir > 0 &&
      y <= NEAR_TOP_Y &&
      !pinned &&
      (cols.left.el.classList.contains('is-offset') ||
        cols.right.el.classList.contains('is-offset'))
    ) {
      hardReset('left');
      hardReset('right');
      pinned = null;
      pinExtremeY = 0;
      lastY = y;
      requestAnimationFrame(captureBaseline);
      return;
    }

    lastY = y;

    if (y <= EPS) {
      const stuck =
        pinned || colIsStuck(cols.left.el) || colIsStuck(cols.right.el);
      if (stuck) {
        hardReset('left');
        hardReset('right');
        pinned = null;
        pinExtremeY = 0;
        requestAnimationFrame(captureBaseline);
      }
      return;
    }

    if (!pinned) {
      const lRect = cols.left.el.getBoundingClientRect();
      const rRect = cols.right.el.getBoundingClientRect();

      if (Math.abs(lRect.height - rRect.height) < EPS) return;

      // Latch: só pin na direção atual (evita re-pin top na descida).
      if (scrollDir > 0) checkFreeDown(lRect, rRect, vh);
      else if (scrollDir < 0) checkFreeUp(lRect, rRect, hb);
      return;
    }

    const key = pinned.key;
    const col = cols[key];
    const other = cols[otherKey(key)];

    if (pinned.edge === 'top' && col.el.classList.contains('is-pin-top')) {
      col.el.style.top = `${hb}px`;
      if (key === 'right') setFoldChrome(hb);
    }

    const colRect = col.el.getBoundingClientRect();
    const otherRect = other.el.getBoundingClientRect();

    if (pinned.edge === 'bottom') {
      if (otherRect.bottom <= colRect.bottom + EPS) handoffToOffset(key);
      return;
    }

    if (otherRect.top >= hb - EPS) handoffToOffset(key);
  }

  /** @param {'left' | 'right'} key */
  function syncSpacerForCol(key) {
    const col = cols[key];
    if (!col.el || !grid) return;

    if (col.el.classList.contains('is-offset')) {
      const gridRect = grid.getBoundingClientRect();
      const r = col.el.getBoundingClientRect();
      setSpacerHeight(key, r.top - gridRect.top + r.height - col.rowStart);
      return;
    }

    if (
      col.el.classList.contains('is-pin-top') ||
      col.el.classList.contains('is-pin-bottom') ||
      col.el.classList.contains('is-pin-freeze')
    ) {
      setSpacerHeight(key, col.el.getBoundingClientRect().height);
    }
  }

  function onScroll() {
    latestY = window.scrollY || 0;
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      evaluate();
    });
  }

  /**
   * Bootstrap sem delta de scroll: se a coluna mais curta já passou do ponto
   * de pin-bottom (ex.: refresh no meio da galeria), aplica o pin agora.
   * Não altera a regra de scroll contínuo (ainda exige direção).
   */
  function reconcileAtRest() {
    if (!hero || !cols.left.el || !cols.right.el) return;
    if (!isDesktop() || !isFluid() || pinned) return;

    const lRect = cols.left.el.getBoundingClientRect();
    const rRect = cols.right.el.getBoundingClientRect();
    const vh = window.innerHeight;

    if (Math.abs(lRect.height - rRect.height) < EPS) return;

    checkFreeDown(lRect, rRect, vh);
  }

  function onPageshow() {
    latestY = window.scrollY || 0;
    lastY = latestY;
    requestAnimationFrame(reconcileAtRest);
  }

  function onScrollRestoreOnce() {
    window.removeEventListener('scroll', onScrollRestoreOnce);
    latestY = window.scrollY || 0;
    lastY = latestY;
    reconcileAtRest();
  }

  function onContentResize() {
    if (!isDesktop() || !isFluid()) {
      hardReset('left');
      hardReset('right');
      pinned = null;
      pinExtremeY = 0;
      clearFoldChrome();
      clearMainLock();
      return;
    }

    const info = cols.right.el;
    if (!info) return;

    const newH = info.getBoundingClientRect().height;
    const delta = newH - lastRightHeight;

    // Info pin-bottom + altura mudou: fixed bottom:0 move o top.
    // Handoff preservando top — grow desce o bottom; shrink recolhe o bottom.
    if (
      Math.abs(delta) > EPS &&
      pinned &&
      pinned.key === 'right' &&
      pinned.edge === 'bottom' &&
      info.classList.contains('is-pin-bottom')
    ) {
      handoffPinBottomPreservingTop('right', delta);
    }

    // Offset: top já fixo; shrink/grow só muda o bottom. Re-sync spacer.
    syncSpacerForCol('left');
    syncSpacerForCol('right');

    lastRightHeight = cols.right.el.getBoundingClientRect().height;

    // Shrink: info mais curta que a galeria — congela no lugar (sem snap a vh)
    // até o bottom das fotos alcançar o bottom da info.
    if (delta < -EPS && cols.left.el) {
      const iRect = cols.right.el.getBoundingClientRect();
      const gRect = cols.left.el.getBoundingClientRect();
      if (iRect.height < gRect.height - EPS && iRect.bottom < gRect.bottom - EPS) {
        freezeAtCurrent('right');
      }
    }

    evaluate();
    // Resize de janela: libera lock, re-mede a 1ª tela, re-trava.
    if (
      (window.scrollY || 0) <= EPS &&
      !colIsStuck(cols.left.el) &&
      !colIsStuck(cols.right.el)
    ) {
      clearMainLock();
      requestAnimationFrame(captureBaseline);
    }
  }

  function onResize() {
    onContentResize();
  }

  function onTabsResize() {
    // Layout pós-toggle de painel: 2 rAF pra altura estável antes do handoff.
    requestAnimationFrame(() => {
      requestAnimationFrame(onContentResize);
    });
  }

  function onVariantNav() {
    hardReset('left');
    hardReset('right');
    pinned = null;
    pinExtremeY = 0;
    scrollDir = 0;
    clearFoldChrome();
    clearMainLock();
    latestY = window.scrollY || 0;
    lastY = latestY;
    if (cols.right.el) {
      lastRightHeight = cols.right.el.getBoundingClientRect().height;
    }
    requestAnimationFrame(() => {
      captureBaseline();
      evaluate();
    });
  }

  function bind() {
    hero = document.querySelector('.gm-pdp-hero[data-gm-pdp-scroll]');
    if (!hero) return;
    grid = /** @type {HTMLElement | null} */ (hero.querySelector('.gm-pdp-hero__grid'));
    cols.left.el = /** @type {HTMLElement | null} */ (hero.querySelector('.gm-pdp-hero__gallery'));
    cols.right.el = /** @type {HTMLElement | null} */ (hero.querySelector('.gm-pdp-hero__info'));
    if (!grid || !cols.left.el || !cols.right.el) return;
    if (!isFluid()) return;

    const gridRect = grid.getBoundingClientRect();
    cols.left.rowStart = cols.left.el.getBoundingClientRect().top - gridRect.top;
    cols.right.rowStart = cols.right.el.getBoundingClientRect().top - gridRect.top;
    lastRightHeight = cols.right.el.getBoundingClientRect().height;

    ensureSpacer('left');
    ensureSpacer('right');
    latestY = window.scrollY || 0;
    lastY = latestY;
    scrollDir = 0;
    pinExtremeY = 0;

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('pageshow', onPageshow);
    document.addEventListener('gm:pdp-tabs-resize', onTabsResize);

    ro = new ResizeObserver(() => onContentResize());
    ro.observe(cols.left.el);
    ro.observe(cols.right.el);

    hero.querySelectorAll('.gm-pdp-showcase__swatches [data-gm-intro-color-thumb]').forEach((el) => {
      el.addEventListener('click', () => {
        setTimeout(onVariantNav, 80);
      });
    });

    evaluate();
    requestAnimationFrame(() => {
      captureBaseline();
      reconcileAtRest();
    });
    // Restauração async do scroll do browser (após paint).
    window.addEventListener('scroll', onScrollRestoreOnce, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
